import type {
  BiddingStrategy,
  CampaignCreationCapabilities,
  Placement,
  SmartCreationReviewAdGroup,
  SmartCreationReviewCampaign,
  SmartCreationReviewEditChange,
  SmartCreationReviewWorkspace,
} from "@adpilot/shared";
import { IconX } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MoneyInput } from "@/components/patterns/MoneyInput";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogPopup,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
} from "@/components/ui/input-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Slider } from "@/components/ui/slider";
import { currentBudgetSuggestion } from "./ReviewBudgetDialog";
import type { ReviewChangeHandler } from "./review-change-handler";

const placements: readonly Placement[] = [
  "PLACEMENT_TOP",
  "PLACEMENT_PRODUCT_PAGE",
  "PLACEMENT_REST_OF_SEARCH",
  "SITE_AMAZON_BUSINESS",
];
const biddingStrategies: readonly BiddingStrategy[] = [
  "LEGACY_FOR_SALES",
  "AUTO_FOR_SALES",
  "MANUAL",
];

interface ReviewEntitySheetProps {
  workspace: SmartCreationReviewWorkspace;
  entity: SmartCreationReviewCampaign | SmartCreationReviewAdGroup;
  capabilities?: CampaignCreationCapabilities;
  disabled: boolean;
  onApply: ReviewChangeHandler;
  onClose(): void;
  onDirtyChange(dirty: boolean): void;
}

export function ReviewEntitySheet({
  workspace,
  entity,
  capabilities,
  disabled,
  onApply,
  onClose,
  onDirtyChange,
}: ReviewEntitySheetProps) {
  const { t, i18n } = useTranslation("smartCreation");
  const campaign = entity.ref.kind === "campaign" ? (entity as SmartCreationReviewCampaign) : null;
  const group = entity.ref.kind === "ad_group" ? (entity as SmartCreationReviewAdGroup) : null;
  const [name, setName] = useState(entity.name.effective);
  const [amount, setAmount] = useState(
    String(campaign?.dailyBudget.effective ?? group?.defaultBid.effective),
  );
  const [bidding, setBidding] = useState<BiddingStrategy>(
    campaign?.biddingStrategy.effective ?? "LEGACY_FOR_SALES",
  );
  const initialPlacements = Object.fromEntries(
    placements.map((placement) => [
      placement,
      String(
        campaign?.placements.find((item) => item.placement === placement)?.percentage.effective ??
          0,
      ),
    ]),
  ) as Record<Placement, string>;
  const [placementValues, setPlacementValues] = useState(initialPlacements);
  const [invalid, setInvalid] = useState(false);
  const [discardOpen, setDiscardOpen] = useState(false);
  const dirty =
    name !== entity.name.effective ||
    amount !== String(campaign?.dailyBudget.effective ?? group?.defaultBid.effective) ||
    (campaign !== null &&
      (bidding !== campaign.biddingStrategy.effective ||
        placements.some(
          (placement) => placementValues[placement] !== initialPlacements[placement],
        )));
  useEffect(() => {
    onDirtyChange(dirty);
  }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  const total = workspace.summary.totalDailyBudget;
  const value = Number(amount);
  const fraction = Number.isFinite(value) && total > 0 ? (value / total) * 100 : 0;
  const currency = new Intl.NumberFormat(i18n.resolvedLanguage, {
    style: "currency",
    currency: workspace.summary.currencyCode,
  });
  const suggested = campaign
    ? currentBudgetSuggestion(workspace)?.find(
        (item) => item.ref.lineageKey === campaign.ref.lineageKey,
      )?.allocation.amount
    : undefined;
  const readOnly = disabled || workspace.status === "confirmed";
  const limits = campaign ? capabilities?.dailyBudget : capabilities?.bid;
  const decimals = limits?.decimals ?? 2;
  const scale = 10 ** decimals;
  const amountRange = (() => {
    if (!limits || campaign?.excluded.effective) return null;
    if (!campaign) return { min: limits.min, max: limits.max };
    // The engine redistributes the remainder, so every other Campaign must stay within limits too.
    const others = workspace.graph.campaigns.filter(
      (item) => !item.excluded.effective && item.ref.lineageKey !== campaign.ref.lineageKey,
    ).length;
    const totalUnits = Math.round(total * scale);
    const minUnits = Math.ceil(limits.min * scale - 1e-9);
    const maxUnits = Math.floor(limits.max * scale + 1e-9);
    return {
      min: Math.max(minUnits, totalUnits - others * maxUnits) / scale,
      max: Math.min(maxUnits, totalUnits - others * minUnits) / scale,
    };
  })();
  const invalidAmount =
    !amount.trim() ||
    !Number.isFinite(value) ||
    value <= 0 ||
    (campaign !== null && !campaign.excluded.effective && value > total) ||
    (amountRange !== null &&
      (value < amountRange.min ||
        value > amountRange.max ||
        Math.abs(value * scale - Math.round(value * scale)) > 1e-6));
  const invalidPlacement = placements.some(
    (placement) =>
      !/^\d+$/.test(placementValues[placement]) || Number(placementValues[placement]) > 900,
  );
  const close = () => {
    if (dirty) setDiscardOpen(true);
    else onClose();
  };

  async function save() {
    if (!name.trim() || name.trim().length > 128 || invalidAmount || invalidPlacement) {
      setInvalid(true);
      return;
    }
    const changes: SmartCreationReviewEditChange[] = [];
    if (campaign) {
      if (name.trim() !== campaign.name.effective)
        changes.push({ operation: "set_campaign_name", ref: campaign.ref, name: name.trim() });
      if (value !== campaign.dailyBudget.effective && !campaign.excluded.effective)
        changes.push({
          operation: "set_campaign_budget_allocation",
          ref: campaign.ref,
          allocation: { basis: "amount", amount: value },
        });
      if (bidding !== campaign.biddingStrategy.effective)
        changes.push({
          operation: "set_campaign_bidding_strategy",
          ref: campaign.ref,
          biddingStrategy: bidding,
        });
      for (const placement of placements) {
        const previous = campaign.placements.find((item) => item.placement === placement);
        const percentage = Number(placementValues[placement]);
        if (percentage !== (previous?.percentage.effective ?? 0))
          changes.push({
            operation: "set_campaign_placement",
            ref: campaign.ref,
            placement,
            percentage,
          });
      }
    } else if (group) {
      if (name.trim() !== group.name.effective)
        changes.push({ operation: "set_ad_group_name", ref: group.ref, name: name.trim() });
      if (value !== group.defaultBid.effective)
        changes.push({ operation: "set_ad_group_default_bid", ref: group.ref, defaultBid: value });
    }
    if ((await onApply(changes)) !== false) onClose();
  }

  return (
    <>
      <Sheet
        open
        onOpenChange={(open) => {
          if (!open && !disabled) close();
        }}
      >
        <SheetContent
          showCloseButton={false}
          className="smart-creation-preview gap-0 overflow-hidden rounded-[14px] border border-line data-[side=right]:inset-y-2 data-[side=right]:right-2 data-[side=right]:h-[calc(100%-16px)] data-[side=right]:w-[min(440px,calc(100vw-16px))] data-[side=right]:sm:max-w-[440px]"
          data-review-entity-sheet
        >
          <SheetHeader className="flex-row items-start justify-between border-b border-line px-6 py-5">
            <div className="min-w-0 space-y-1">
              <SheetTitle className="text-base leading-[22px]">
                {t(campaign ? "review.preview.editCampaign" : "review.preview.editGroup")}
              </SheetTitle>
              <SheetDescription className="truncate text-xs leading-[18px]">
                {entity.name.effective}
              </SheetDescription>
            </div>
            <Button
              variant="outline"
              size="icon-sm"
              disabled={disabled}
              aria-label={t("review.parity.close")}
              onClick={close}
            >
              <IconX />
            </Button>
          </SheetHeader>
          <div className="paper-scrollbar flex min-h-0 flex-1 flex-col gap-[18px] overflow-y-auto px-6 py-5">
            <Field invalid={invalid && !name.trim()} className="gap-[7px]">
              <FieldLabel htmlFor="review-entity-name">
                {t(campaign ? "review.fields.campaignName" : "review.fields.adGroupName")}
              </FieldLabel>
              <Input
                id="review-entity-name"
                value={name}
                maxLength={128}
                disabled={readOnly}
                onChange={(event) => {
                  setName(event.target.value);
                  setInvalid(false);
                }}
              />
              {invalid && !name.trim() ? (
                <FieldError match>{t("review.summary.invalidName")}</FieldError>
              ) : null}
            </Field>
            <Field invalid={invalid && invalidAmount} className="gap-[9px]">
              <FieldLabel htmlFor="review-entity-amount">
                {t(campaign ? "review.fields.budgetAllocation" : "review.fields.defaultBid")}
              </FieldLabel>
              <div className="flex w-full items-center justify-between gap-4">
                {campaign ? (
                  <span className="text-lg font-semibold tabular-nums">{fraction.toFixed(2)}%</span>
                ) : null}
                <MoneyInput
                  id="review-entity-amount"
                  type="number"
                  min={amountRange?.min ?? 1 / scale}
                  max={amountRange?.max}
                  step={1 / scale}
                  size="default"
                  value={amount}
                  containerClassName={campaign ? "w-28" : "w-full"}
                  disabled={readOnly || campaign?.excluded.effective}
                  onChange={(event) => {
                    setAmount(event.target.value);
                    setInvalid(false);
                  }}
                />
              </div>
              {campaign ? (
                <>
                  <Slider
                    className="flex h-[22px] items-center [&_[data-slot=slider-indicator]]:bg-ink [&_[data-slot=slider-thumb]]:size-[18px]"
                    value={Math.min(100, Math.max(0, fraction))}
                    min={0}
                    max={100}
                    step={0.01}
                    disabled={
                      readOnly ||
                      campaign.excluded.effective ||
                      workspace.summary.includedCampaignCount < 2
                    }
                    aria-label={t("review.fields.budgetAllocation")}
                    onValueChange={(next) => {
                      const requested = Math.round((total * Number(next) * scale) / 100) / scale;
                      const clamped = amountRange
                        ? Math.min(amountRange.max, Math.max(amountRange.min, requested))
                        : requested;
                      setAmount(clamped.toFixed(decimals));
                      setInvalid(false);
                    }}
                  />
                  <p className="text-[11px] leading-[18px] text-muted-foreground">
                    {t("review.preview.budgetOthers", {
                      total: currency.format(total),
                      other: currency.format(total - (Number.isFinite(value) ? value : 0)),
                    })}
                  </p>
                </>
              ) : null}
              {invalid && invalidAmount ? (
                <FieldError match>
                  {amountRange
                    ? t("review.preview.amountRange", {
                        min: currency.format(amountRange.min),
                        max: currency.format(amountRange.max),
                        decimals,
                      })
                    : t("review.parity.invalidBudget")}
                </FieldError>
              ) : null}
            </Field>
            {campaign && suggested !== undefined ? (
              <div className="flex items-center justify-between gap-3 rounded-lg bg-subtle p-3">
                <div className="space-y-1">
                  <Badge variant="neutral">{t("review.preview.aiSuggestion")}</Badge>
                  <p className="text-xs">
                    {currency.format(suggested)} · {((suggested / total) * 100).toFixed(2)}%
                  </p>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={readOnly || campaign.excluded.effective}
                  onClick={() => {
                    setAmount(String(suggested));
                  }}
                >
                  {t("review.summary.budgetSuggestion.apply")}
                </Button>
              </div>
            ) : null}
            {campaign ? (
              <>
                <Field className="gap-[7px]">
                  <FieldLabel>{t("review.inspector.biddingStrategy")}</FieldLabel>
                  <Select
                    value={bidding}
                    disabled={readOnly}
                    onValueChange={(next) => {
                      if (next) {
                        setBidding(next);
                      }
                    }}
                  >
                    <SelectTrigger
                      className="w-full"
                      aria-label={t("review.inspector.biddingStrategy")}
                    >
                      <SelectValue>{t(`review.bidding.${bidding}`)}</SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {biddingStrategies.map((strategy) => (
                        <SelectItem key={strategy} value={strategy}>
                          {t(`review.bidding.${strategy}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <section className="space-y-3 border-t border-line pt-4">
                  <h3 className="text-[13px] font-semibold">{t("review.inspector.placements")}</h3>
                  <p className="text-[11px] leading-[18px] text-muted-foreground">
                    {t("review.preview.placementHint")}
                  </p>
                  {placements.map((placement) => (
                    <Field
                      key={placement}
                      invalid={invalid && invalidPlacement}
                      orientation="horizontal"
                      className="justify-between"
                    >
                      <FieldLabel htmlFor={`review-placement-${placement}`} className="text-xs">
                        {t(`review.placement.${placement}`)}
                      </FieldLabel>
                      <InputGroup className="w-24">
                        <InputGroupInput
                          id={`review-placement-${placement}`}
                          type="number"
                          min={0}
                          max={900}
                          step={1}
                          value={placementValues[placement]}
                          disabled={readOnly}
                          onChange={(event) => {
                            setPlacementValues((current) => ({
                              ...current,
                              [placement]: event.target.value,
                            }));
                          }}
                        />
                        <InputGroupAddon align="inline-end">
                          <InputGroupText>%</InputGroupText>
                        </InputGroupAddon>
                      </InputGroup>
                    </Field>
                  ))}
                  {invalid && invalidPlacement ? (
                    <p role="alert" className="text-xs text-destructive">
                      {t("review.fields.invalidPlacement")}
                    </p>
                  ) : null}
                </section>
              </>
            ) : null}
          </div>
          <SheetFooter className="flex-row justify-end gap-2 border-t border-line bg-table-footer px-6 py-4">
            <Button variant="outline" size="sm" disabled={disabled} onClick={close}>
              {t("review.parity.cancel")}
            </Button>
            {workspace.status !== "confirmed" ? (
              <Button size="sm" disabled={readOnly || !dirty} onClick={() => void save()}>
                {t("review.preview.saveChanges")}
              </Button>
            ) : null}
          </SheetFooter>
        </SheetContent>
      </Sheet>
      <AlertDialog open={discardOpen} onOpenChange={setDiscardOpen}>
        <AlertDialogPopup>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("review.preview.discardTitle")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("review.preview.discardDescription")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button variant="outline" size="sm" onClick={() => setDiscardOpen(false)}>
              {t("review.preview.keepEditing")}
            </Button>
            <Button variant="destructive" size="sm" onClick={onClose}>
              {t("review.preview.discard")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogPopup>
      </AlertDialog>
    </>
  );
}
